import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import classes from "./styles.module.scss";
import categoryItemClasses from "./categoryItem.module.scss";
import { useDispatch } from "react-redux";
import { DropdownItem } from "Components/ReactstrapSlim";
import { Modifiers } from "popper.js";
import { Caret } from "Components/Icons";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { getScrollbarWidth } from "Utils/getScrollbarWidth";
import { DropdownContext } from "reactstrap/lib/DropdownContext";
import { CATEGORIES } from "../components";
import { EnterpriseDemoRequestMode } from "Services/API/API";
import { ContactUsModal, FormType } from "Components/Modal";

const scrollbarWidth = getScrollbarWidth();

export const modifiers: Modifiers = {
   offset: { enabled: true, offset: "0,0" },
   preventOverflow: {
      enabled: true,
      padding: {
         left: 0
      }
   },
   flip: {
      enabled: false
   }
};

interface HeaderMobileAnonymousDropDownMenuProps {
   redirectToMyLiquidSpaceAfterSignIn?: boolean;
   isOpen: boolean;
   menuPortalElement: HTMLElement;
}

const HeaderMobileAnonymousDropDownMenu: React.FC<HeaderMobileAnonymousDropDownMenuProps> = ({
   isOpen,
   redirectToMyLiquidSpaceAfterSignIn,
   menuPortalElement }) => {
   const dispatch = useDispatch();
   const openLoginPopup = (signingMode: LSStore.SigningMode) => {
      dispatch(LSStore.signingModalSlice.actions.open({
         initialMode: signingMode,
         popUpRedirectUrl: redirectToMyLiquidSpaceAfterSignIn ? "/my/index" : undefined
      }));
   }

   const [isSolutionsCategoriesVisible, setSolutionsCategoriesVisible] = useState(false);

   const toggleSolutions = useCallback(() => {
      setSolutionsCategoriesVisible(x => !x);
   }, [setSolutionsCategoriesVisible]);

   const [enterpriseRequestModalOpened, setEnterpriseRequestModalOpened] = useState(false);
   const toggleEnterpriseRequestModal = () => {
      setEnterpriseRequestModalOpened((wasOpened) => !wasOpened);
   }

   const categories = useMemo(() => CATEGORIES.flat(1), []);
   
   const { onMenuRef } = useContext(DropdownContext);

   useEffect(() => {
      menuPortalElement.classList.toggle(classes.menuPortalElement, true);

      // Send the ref to the parent Dropdown so that clicks outside
      // it will cause it to close
      onMenuRef(menuPortalElement);

      return () => { menuPortalElement.classList.toggle(classes.menuPortalElement, false); };
   }, []);

   useEffect(() => {
      if (isOpen) {
         const bodyPaddingRightInitial = document.body.style.paddingRight;
         document.body.style.paddingRight = (bodyPaddingRightInitial + scrollbarWidth) + "px";
         document.body.classList.toggle(classes.bodyOverflowHidden, true);

         menuPortalElement.classList.toggle(classes.menuPortalElementVisible, true);

         setSolutionsCategoriesVisible(false);

         return () => {
            document.body.style.paddingRight = bodyPaddingRightInitial;
            document.body.classList.toggle(classes.bodyOverflowHidden, false);

            menuPortalElement.classList.toggle(classes.menuPortalElementVisible, false);
         }
      }
   }, [isOpen]);

   
   return createPortal(<div className={classNames(classes.menu)}>
      <div className={classes.solutions}>
         <button
            className={classNames(classes["dropdown-item"], classes.solutionsToggle, { [classes.solutionsToggleOpened]: isSolutionsCategoriesVisible })}
            onClick={toggleSolutions}
         >
            Solutions
            <Caret size={24} />
         </button>
         <div className={classNames(classes.solutionsCategories, { [classes.solutionsCategoriesVisible]: isSolutionsCategoriesVisible })}>
            {
               categories.map(category => <div key={category.title} className={classes.category}>
                  <div className={classes.solutionsCategoryTitle}>
                     {category.title}
                  </div>
                  <div className={categoryItemClasses.items}>
                     {
                        category.items.map(item => <DropdownItem key={item.title} href={item.href} cssModule={categoryItemClasses}>
                           {item.title}
                        </DropdownItem>)
                     }
                  </div>
               </div>)
            }
         </div>
      </div>
      <DropdownItem cssModule={classes} href="/how-it-works">
         How it Works
      </DropdownItem>
      <DropdownItem cssModule={classes} href="https://resources.liquidspace.com">
         Resources
      </DropdownItem>
      <DropdownItem cssModule={classes} href="/pricing">
         Pricing
      </DropdownItem>
      <DropdownItem cssModule={classes} onClick={toggleEnterpriseRequestModal}>
         Get demo
      </DropdownItem>
      <DropdownItem cssModule={classes} divider />
      <DropdownItem cssModule={classes} onClick={() => openLoginPopup(LSStore.SigningMode.Login)}>
         Log in
      </DropdownItem>
      <DropdownItem cssModule={classes} onClick={() => openLoginPopup(LSStore.SigningMode.Register)}>
         Sign up
      </DropdownItem>
      <DropdownItem cssModule={classes} divider />
      <DropdownItem cssModule={classes} tag="a" href="/share-office-meeting-space">
         Become a Host
      </DropdownItem>
      <DropdownItem cssModule={classes} tag="a" href="https://support.liquidspace.com">
         Help
      </DropdownItem>
      {enterpriseRequestModalOpened && <ContactUsModal toggle={toggleEnterpriseRequestModal} isOpen={enterpriseRequestModalOpened} formType={FormType.Specific} formName="Get demo" mode={EnterpriseDemoRequestMode.Enterprise} centered />}
   </div>, menuPortalElement);
}

export default HeaderMobileAnonymousDropDownMenu;